import {
  buildTourPartyMemberBlock,
  parseWriterMemberBlocks,
} from './writerFormat';
import {
  normalizeEmail,
  tourPartyMemberBlockId,
} from './tourPartyMemberUtils';
import { serializeWriterDoc } from './writerEditorExtensions';

/** Split stored HTML into body HTML and member node attrs. */
export function splitWriterHtml(html) {
  if (typeof document === 'undefined') {
    return { bodyHtml: String(html || ''), members: [] };
  }
  const root = document.createElement('div');
  root.innerHTML = String(html || '');
  const members = [];
  root.querySelectorAll('.vrc-writer-member').forEach((el) => {
    members.push({
      memberId: el.getAttribute('data-member-id'),
      blockHtml: el.outerHTML,
    });
    el.remove();
  });
  const bodyHtml = root.innerHTML.trim() || '<p></p>';
  return { bodyHtml, members };
}

/** Load full rider HTML into a TipTap editor (members as atom nodes). */
export function loadHtmlIntoWriterEditor(editor, html) {
  if (!editor) return;
  const { bodyHtml, members } = splitWriterHtml(html);
  editor.commands.setContent(bodyHtml, false);
  if (members.length) {
    const nodes = members.map((attrs) => ({
      type: 'writerMember',
      attrs: {
        memberId: attrs.memberId,
        blockHtml: attrs.blockHtml,
      },
    }));
    editor.commands.insertContent(nodes);
  }
}

export function getWriterEditorHtml(editor) {
  return serializeWriterDoc(editor);
}

export function writerMemberExistsInEditor(editor, member) {
  if (!editor || !member) return false;
  const blockId = tourPartyMemberBlockId(member);
  const uid = String(member.id || '').trim();
  const email = normalizeEmail(member.email);
  let found = false;
  editor.state.doc.descendants((node) => {
    if (found || node.type.name !== 'writerMember') return;
    const id = String(node.attrs.memberId || '').trim();
    if (id && (id === blockId || (uid && id === uid))) {
      found = true;
      return false;
    }
    const temp = document.createElement('div');
    temp.innerHTML = node.attrs.blockHtml || '';
    const existingEmail = normalizeEmail(
      temp.querySelector('.vrc-writer-member-email')?.textContent || ''
    );
    if (email && (existingEmail === email || normalizeEmail(id) === email)) {
      found = true;
      return false;
    }
    return undefined;
  });
  return found;
}

export function insertWriterMember(editor, member) {
  if (!editor || !member) return false;
  const blockHtml = buildTourPartyMemberBlock(member);
  editor
    .chain()
    .focus('end')
    .insertContent({
      type: 'writerMember',
      attrs: {
        memberId: tourPartyMemberBlockId(member),
        blockHtml,
      },
    })
    .run();
  return true;
}

export function removeWriterMember(editor, member) {
  if (!editor || !member) return;
  const blockId = tourPartyMemberBlockId(member);
  const uid = String(member.id || '').trim();
  const email = normalizeEmail(member.email);
  const ranges = [];

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'writerMember') return;
    const id = String(node.attrs.memberId || '').trim();
    const temp = document.createElement('div');
    temp.innerHTML = node.attrs.blockHtml || '';
    const existingEmail = normalizeEmail(
      temp.querySelector('.vrc-writer-member-email')?.textContent || ''
    );
    const match =
      (blockId && id === blockId) ||
      (uid && id === uid) ||
      (email && (existingEmail === email || normalizeEmail(id) === email));
    if (match) ranges.push({ from: pos, to: pos + node.nodeSize });
  });

  if (!ranges.length) return;
  const chain = editor.chain().focus();
  ranges
    .sort((a, b) => b.from - a.from)
    .forEach(({ from, to }) => chain.deleteRange({ from, to }));
  chain.run();
}

/** Member list from editor state (same shape as parseWriterMemberBlocks). */
export function parseWriterMembersFromEditor(editor) {
  if (!editor) return parseWriterMemberBlocks('');
  return parseWriterMemberBlocks(getWriterEditorHtml(editor));
}
